import { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Search, X, Download, Trash2 } from "lucide-react";
import type { SelectedColor } from "@/components/colors/ColorPicker";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type RawColor = {
  id: string;
  color_number: string;
  color_name: string;
  hex: string;
  red: number;
  green: number;
  blue: number;
  lrv: number | null;
  category: string | null;
  paint_brands: { name: string } | null;
  paint_collections: { name: string } | null;
  swatch_url: string | null;
};

/* ------------------------------------------------------------------ */
/*  HSL helpers                                                        */
/* ------------------------------------------------------------------ */

function rgbToHsl(r: number, g: number, b: number): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  const d = max - min;
  if (d !== 0) {
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return [h, s, l];
}

/* ------------------------------------------------------------------ */
/*  Filters                                                            */
/* ------------------------------------------------------------------ */

const FILTERS = [
  { id: "all", label: "ALL" },
  { id: "red", label: "RED" },
  { id: "orange", label: "ORANGE" },
  { id: "yellow", label: "YELLOW" },
  { id: "green", label: "GREEN" },
  { id: "blue", label: "BLUE" },
  { id: "purple", label: "PURPLE" },
  { id: "neutral", label: "NEUTRAL" },
  { id: "brown", label: "BROWN" },
  { id: "white", label: "WHITE" },
  { id: "gray", label: "GRAY" },
  { id: "black", label: "BLACK" },
] as const;

function matchesFilter(color: RawColor, filterId: string): boolean {
  if (filterId === "all") return true;
  const [h, s, l] = rgbToHsl(color.red, color.green, color.blue);
  const cat = (color.category ?? "").toLowerCase();

  switch (filterId) {
    case "red":
      return (h < 0.042 || h > 0.958) && s > 0.12;
    case "orange":
      return h >= 0.042 && h < 0.125 && s > 0.12;
    case "yellow":
      return h >= 0.125 && h < 0.167 && s > 0.12;
    case "green":
      return h >= 0.167 && h < 0.458 && s > 0.12;
    case "blue":
      return h >= 0.458 && h < 0.708 && s > 0.12;
    case "purple":
      return h >= 0.708 && h < 0.958 && s > 0.12;
    case "neutral":
      return s < 0.12;
    case "brown":
      return (
        cat.includes("brown") ||
        cat.includes("tan") ||
        cat.includes("beige") ||
        ((h < 0.125 || h > 0.958) && s > 0.06 && s < 0.35 && l > 0.15 && l < 0.55)
      );
    case "white":
      return l > 0.92;
    case "gray":
      return s < 0.1 && l >= 0.15 && l <= 0.92;
    case "black":
      return l < 0.15;
    default:
      return true;
  }
}

/* ------------------------------------------------------------------ */
/*  My Colors localStorage                                             */
/* ------------------------------------------------------------------ */

const MY_KEY = "mp_my_colors";

function readMyColors(): SelectedColor[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(MY_KEY);
    return raw ? (JSON.parse(raw) as SelectedColor[]) : [];
  } catch {
    return [];
  }
}

function writeMyColors(list: SelectedColor[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(MY_KEY, JSON.stringify(list.slice(0, 50)));
  } catch {
    /* noop */
  }
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function ColorExplorer({ onPick }: { onPick?: (c: SelectedColor) => void }) {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<string>("all");
  const [myColors, setMyColors] = useState<SelectedColor[]>(() => readMyColors());

  const colors = useQuery({
    queryKey: ["paint-colors-spectrum"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("paint_colors")
        .select(
          "id, color_number, color_name, hex, red, green, blue, lrv, category, swatch_url, paint_brands(name), paint_collections(name)",
        )
        .eq("is_active", true)
        .order("color_number")
        .limit(3000);
      if (error) throw error;
      return (data ?? []) as unknown as RawColor[];
    },
    staleTime: 5 * 60_000,
  });

  const term = query.trim().toLowerCase();

  const processed = useMemo(() => {
    const rows = colors.data ?? [];
    return rows.map((r) => {
      const [hue, sat, lig] = rgbToHsl(r.red, r.green, r.blue);
      return { ...r, hue, sat, lig };
    });
  }, [colors.data]);

  const spectrum = useMemo(() => {
    const filtered = processed.filter((c) => {
      if (term) {
        const hay = `${c.color_name} ${c.color_number} ${c.category ?? ""}`.toLowerCase();
        if (!hay.includes(term)) return false;
      }
      return matchesFilter(c, filter);
    });

    // Split into vivid spectrum vs neutrals
    const vivid = filtered.filter((c) => c.sat >= 0.12);
    const neutrals = filtered.filter((c) => c.sat < 0.12);

    // Sort vivid by hue, then saturation desc, then lightness desc
    vivid.sort((a, b) => {
      if (a.hue !== b.hue) return a.hue - b.hue;
      if (a.sat !== b.sat) return b.sat - a.sat;
      return b.lig - a.lig;
    });

    // Sort neutrals by lightness desc
    neutrals.sort((a, b) => b.lig - a.lig);

    return { vivid, neutrals };
  }, [processed, filter, term]);

  const myIds = useMemo(() => new Set(myColors.map((c) => c.id)), [myColors]);

  const toggleMyColor = useCallback((row: RawColor) => {
    const sc: SelectedColor = {
      id: row.id,
      brand: row.paint_brands?.name ?? "Sherwin-Williams",
      collection: row.paint_collections?.name ?? null,
      color_number: row.color_number,
      color_name: row.color_name,
      hex: row.hex,
      red: row.red,
      green: row.green,
      blue: row.blue,
      lrv: row.lrv,
      swatch_url: row.swatch_url,
    };

    setMyColors((prev) => {
      const exists = prev.some((c) => c.id === sc.id);
      const next = exists ? prev.filter((c) => c.id !== sc.id) : [sc, ...prev].slice(0, 50);
      writeMyColors(next);
      return next;
    });

    onPick?.(sc);
  }, [onPick]);

  const removeMyColor = useCallback((id: string) => {
    setMyColors((prev) => {
      const next = prev.filter((c) => c.id !== id);
      writeMyColors(next);
      return next;
    });
  }, []);

  const clearMyColors = useCallback(() => {
    setMyColors([]);
    writeMyColors([]);
  }, []);

  const totalDisplayed = spectrum.vivid.length + spectrum.neutrals.length;

  return (
    <div className="space-y-8">
      {/* Search + filters */}
      <div className="space-y-4">
        {/* Search */}
        <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/10 focus-within:border-amber-300/40 transition-colors max-w-md">
          <Search className="size-4 text-zinc-400 shrink-0" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, SW number, or category…"
            className="bg-transparent outline-none text-sm text-white placeholder:text-zinc-500 flex-1 min-w-0"
          />
          {query && (
            <button onClick={() => setQuery("")} className="text-zinc-400 hover:text-white">
              <X className="size-3.5" />
            </button>
          )}
        </div>

        {/* Filter chips */}
        <div className="flex items-center gap-1.5 flex-wrap">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              onClick={() => setFilter(f.id)}
              className={`text-[11px] px-3 py-1.5 rounded-full border transition uppercase tracking-wider font-medium ${
                filter === f.id
                  ? "border-amber-300 bg-amber-300 text-zinc-950"
                  : "border-white/10 text-zinc-300 hover:border-white/30 hover:text-white"
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Status */}
      {colors.isError && (
        <div className="rounded-xl border border-red-400/30 bg-red-500/[0.08] p-4">
          <p className="text-sm text-red-200">Failed to load colors. {colors.error instanceof Error ? colors.error.message : String(colors.error)}</p>
        </div>
      )}

      <p className="text-[11px] text-zinc-500 uppercase tracking-wider">
        {totalDisplayed.toLocaleString()} colors shown
      </p>

      {/* Spectrum grid */}
      {colors.isLoading && (
        <div className="grid grid-cols-8 sm:grid-cols-12 md:grid-cols-16 lg:grid-cols-20 gap-px">
          {Array.from({ length: 120 }).map((_, i) => (
            <div key={i} className="aspect-square bg-white/5 animate-pulse" />
          ))}
        </div>
      )}

      {!colors.isLoading && !colors.isError && totalDisplayed === 0 && (
        <div className="text-center py-20 text-zinc-500 text-sm">No colors match your search.</div>
      )}

      {/* Vivid spectrum */}
      {spectrum.vivid.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Color Spectrum</p>
          <div className="flex flex-wrap gap-px rounded-xl overflow-hidden bg-white/5 border border-white/5">
            {spectrum.vivid.map((c) => (
              <ColorSwatch
                key={c.id}
                color={c}
                isActive={myIds.has(c.id)}
                onClick={() => toggleMyColor(c)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Neutrals */}
      {spectrum.neutrals.length > 0 && (
        <div className="space-y-2">
          <p className="text-[10px] uppercase tracking-widest text-zinc-500">Neutrals</p>
          <div className="flex flex-wrap gap-px rounded-xl overflow-hidden bg-white/5 border border-white/5">
            {spectrum.neutrals.map((c) => (
              <ColorSwatch
                key={c.id}
                color={c}
                isActive={myIds.has(c.id)}
                onClick={() => toggleMyColor(c)}
              />
            ))}
          </div>
        </div>
      )}

      {/* My Colors */}
      <div className="border-t border-white/5 pt-8 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-white">My Colors</p>
            <p className="text-[11px] text-zinc-500">Click any swatch above to add it here.</p>
          </div>
          {myColors.length > 0 && (
            <div className="flex items-center gap-2">
              <button
                onClick={exportMyColors}
                className="text-[11px] text-zinc-400 hover:text-white inline-flex items-center gap-1"
              >
                <Download className="size-3" /> Export
              </button>
              <button
                onClick={clearMyColors}
                className="text-[11px] text-zinc-400 hover:text-red-300 inline-flex items-center gap-1"
              >
                <Trash2 className="size-3" /> Clear
              </button>
            </div>
          )}
        </div>

        {myColors.length === 0 ? (
          <div className="grid grid-cols-6 sm:grid-cols-10 md:grid-cols-12 gap-2">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[3/4] rounded-lg border border-white/5 bg-white/[0.02]"
              />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-6 sm:grid-cols-10 md:grid-cols-12 gap-2">
            {myColors.map((c) => (
              <div
                key={c.id}
                className="group relative aspect-[3/4] rounded-lg border border-white/10 overflow-hidden"
              >
                <div
                  className="absolute inset-0"
                  style={{ backgroundColor: c.hex }}
                />
                {c.swatch_url && (
                  <img src={c.swatch_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
                )}
                <button
                  onClick={() => removeMyColor(c.id)}
                  className="absolute top-1 right-1 p-1 rounded-full bg-black/60 text-white opacity-0 group-hover:opacity-100 transition"
                >
                  <X className="size-3" />
                </button>
                <div className="absolute inset-x-0 bottom-0 px-2 py-1.5 bg-gradient-to-t from-black/70 to-transparent">
                  <p className="text-[9px] text-white/90 truncate font-medium">{c.color_name}</p>
                  <p className="text-[9px] text-white/60 truncate">{c.color_number}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Swatch                                                             */
/* ------------------------------------------------------------------ */

function ColorSwatch({
  color,
  isActive,
  onClick,
}: {
  color: RawColor & { hue: number; sat: number; lig: number };
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={`${color.color_name} · ${color.color_number}`}
      className={`relative w-10 h-10 sm:w-11 sm:h-11 transition hover:scale-110 hover:z-10 focus:outline-none ${
        isActive ? "ring-2 ring-amber-300 ring-inset z-10" : ""
      }`}
      style={{ backgroundColor: color.hex }}
    >
      {color.swatch_url && (
        <img src={color.swatch_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/*  Export                                                             */
/* ------------------------------------------------------------------ */

function exportMyColors() {
  const list = readMyColors();
  if (list.length === 0) return;
  const csv = ["Name,Number,Hex,Brand,LRV"]
    .concat(
      list.map((c) => `"${c.color_name}","${c.color_number}","${c.hex}","${c.brand}","${c.lrv ?? ""}"`),
    )
    .join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "my-colors.csv";
  a.click();
  URL.revokeObjectURL(url);
}
